import { useEffect, useState } from 'react';
import { studioRecipesApi, type GitLabProjectSearchHit } from '../../lib/studioRecipesApi';

interface Props {
  /** Default search query (typically the studio's display name). */
  initialQuery: string;
  onClose: () => void;
  /** Fill Project id + Branch on the recipe form; caller still saves. */
  onPick: (hit: GitLabProjectSearchHit) => void;
}

/**
 * Modal for mapping a studio recipe to a GitLab project under the
 * client-critical-files group. Search defaults to the studio name; picking
 * a hit only fills the form fields (no remote write until Save recipe).
 */
export function GitLabPickerDialog({ initialQuery, onClose, onPick }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [results, setResults] = useState<GitLabProjectSearchHit[]>([]);

  const runSearch = async (q: string) => {
    setSearching(true);
    setSearchError(null);
    try {
      const data = await studioRecipesApi.gitlabSearch(q);
      setResults(data);
    } catch (err) {
      setSearchError((err as Error).message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (initialQuery.trim().length >= 2) {
      void runSearch(initialQuery);
    }
  }, [initialQuery]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>GIT auto project map</h2>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="muted small" style={{ marginTop: 0 }}>
          Search projects under <code>client-critical-files</code>. Names are matched
          loosely (spaces vs hyphens, case-insensitive). Picking a result fills Project
          id and sets Branch to <code>master</code> (where the game archives live) —
          click SAVE to persist.
        </p>

        <div className="row" style={{ gap: 8 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void runSearch(query);
            }}
            placeholder="Studio name (e.g. Elephant Gaming)"
            style={{ flex: 1 }}
            autoFocus
          />
          <button
            className="btn btn-primary"
            onClick={() => void runSearch(query)}
            disabled={searching || query.trim().length < 2}
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {searchError && (
          <div className="card" style={{ borderColor: 'var(--bad)', marginTop: 12 }}>
            {searchError}
          </div>
        )}

        <div className="flex-col" style={{ gap: 6, marginTop: 12 }}>
          {!searching && results.length === 0 && !searchError && (
            <p className="muted small" style={{ margin: 0 }}>
              {query.trim() ? 'No results. Try a different query.' : 'Type a query and press Search.'}
            </p>
          )}
          {results.map((hit) => (
            <button
              key={hit.id}
              type="button"
              className="card"
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                padding: '10px 12px',
                border: '1px solid var(--border)',
              }}
              onClick={() => onPick(hit)}
            >
              <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                <strong>{hit.name}</strong>
                <span className="muted small mono">#{hit.id}</span>
                <span className="spacer" />
                <span className="muted small">score {hit.matchScore}</span>
              </div>
              <div className="muted small mono" style={{ marginTop: 4 }}>
                {hit.pathWithNamespace}
                {' · master'}
              </div>
              {hit.webUrl ? (
                <a
                  className="small"
                  href={hit.webUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-block', marginTop: 4 }}
                >
                  Open in GitLab
                </a>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
